import { useState } from 'react'
import type { Card } from '../../types'
import { useListsStore, countCards } from '../../stores/listsStore'
import type { DeckList } from '../../stores/listsStore'
import { parseDecklist, decklistToText } from '../../utils/decklist'
import Button from '../ui/Button'

interface ListEditorProps {
  list: DeckList
  /** Called when the user wants to generate a PDF for this list. */
  onGeneratePdf: (list: DeckList, includeSideboard: boolean) => void
}

function CardRow({
  card,
  zone,
  index,
  listId,
}: {
  card: Card
  zone: 'main' | 'side'
  index: number
  listId: string
}) {
  const changeQuantity = useListsStore((s) => s.changeQuantity)
  const removeCard = useListsStore((s) => s.removeCard)
  const updateCard = useListsStore((s) => s.updateCard)

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Create a local URL for preview; the actual upload happens on job creation.
    const localUrl = URL.createObjectURL(file)
    updateCard(listId, zone, index, { ...card, image_url: localUrl, _localFile: file })
  }

  const clearLocalImage = () => {
    updateCard(listId, zone, index, { ...card, image_url: null, _localFile: undefined })
  }

  const hasLocalImage = card.image_url && !card.image_url.startsWith('http')
  const isUnresolved = card.unresolved === true

  return (
    <div className={`flex items-center justify-between py-1.5 px-2 rounded group border-l-4 ${
      isUnresolved
        ? 'bg-red-100 border-red-500 hover:bg-red-200'
        : 'hover:bg-gray-50 border-transparent'
    }`}>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`text-sm font-bold w-8 text-right shrink-0 ${
          isUnresolved ? 'text-red-700' : 'text-gray-700'
        }`}>
          {card.quantity}×
        </span>
        <span className={`text-sm truncate ${
          isUnresolved ? 'text-red-800 font-semibold' : 'text-gray-900'
        }`}>
          {card.name}
          {isUnresolved && (
            <span className="ml-1 text-xs font-bold text-red-600 uppercase">
              ⚠️ No encontrada en base de datos
            </span>
          )}
        </span>
        {card.set_code && (
          <span className="text-xs text-gray-400 shrink-0">({card.set_code.toUpperCase()})</span>
        )}
        {hasLocalImage && (
          <span className="text-xs text-green-600 shrink-0" title="Imagen local subida">
            📷
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        {/* Upload local image */}
        <label
          className="w-6 h-6 rounded border border-blue-200 text-blue-500 hover:bg-blue-50 text-sm leading-none cursor-pointer flex items-center justify-center"
          title="Subir imagen local"
        >
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
          📷
        </label>
        {hasLocalImage && (
          <button
            className="w-6 h-6 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm leading-none"
            onClick={clearLocalImage}
            title="Quitar imagen local"
          >
            ↩
          </button>
        )}
        <button
          className="w-6 h-6 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm leading-none"
          onClick={() => changeQuantity(listId, zone, index, -1)}
          title="Quitar una"
        >
          −
        </button>
        <button
          className="w-6 h-6 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm leading-none"
          onClick={() => changeQuantity(listId, zone, index, 1)}
          title="Añadir una"
        >
          +
        </button>
        <button
          className="w-6 h-6 rounded border border-red-200 text-red-500 hover:bg-red-50 text-sm leading-none"
          onClick={() => removeCard(listId, zone, index)}
          title="Eliminar carta"
        >
          ×
        </button>
      </div>
    </div>
  )
}

export default function ListEditor({ list, onGeneratePdf }: ListEditorProps) {
  const renameList = useListsStore((s) => s.renameList)
  const setListCards = useListsStore((s) => s.setListCards)
  const removeList = useListsStore((s) => s.removeList)

  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState(list.name)
  const [reParsing, setReParsing] = useState(false)
  const [reParseText, setReParseText] = useState('')
  const [includeSide, setIncludeSide] = useState(true)

  const mainCount = countCards(list.mainboard)
  const sideCount = countCards(list.sideboard)

  const saveName = () => {
    const n = nameDraft.trim()
    if (n && n !== list.name) renameList(list.id, n)
    setEditingName(false)
  }

  const startReparse = () => {
    setReParseText(decklistToText(list.mainboard, list.sideboard))
    setReParsing(true)
  }

  const applyReparse = () => {
    const { mainboard, sideboard } = parseDecklist(reParseText)
    setListCards(list.id, mainboard, sideboard)
    setReParsing(false)
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-white">
      {/* Header: name + actions */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {editingName ? (
            <div className="flex gap-2">
              <input
                autoFocus
                className="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveName()
                  if (e.key === 'Escape') setEditingName(false)
                }}
              />
              <Button onClick={saveName}>Guardar</Button>
            </div>
          ) : (
            <button
              className="text-lg font-semibold text-gray-900 hover:text-blue-600 text-left truncate"
              onClick={() => {
                setNameDraft(list.name)
                setEditingName(true)
              }}
              title="Renombrar"
            >
              {list.name}
            </button>
          )}
          <p className="text-xs text-gray-500 mt-0.5">
            {mainCount} cartas{sideCount > 0 ? ` + ${sideCount} sideboard` : ''}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button onClick={startReparse}>Re-pegar</Button>
          <button
            className="px-3 py-1.5 text-sm rounded border border-red-200 text-red-600 hover:bg-red-50"
            onClick={() => {
              if (window.confirm(`¿Borrar la lista "${list.name}"?`)) removeList(list.id)
            }}
          >
            Borrar
          </button>
        </div>
      </div>

      {/* Re-parse mode */}
      {reParsing ? (
        <div className="space-y-2">
          <textarea
            className="w-full h-56 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            value={reParseText}
            onChange={(e) => setReParseText(e.target.value)}
          />
          <div className="flex gap-2">
            <Button onClick={applyReparse}>Aplicar cambios</Button>
            <button
              className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
              onClick={() => setReParsing(false)}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Mainboard */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Main ({mainCount})
            </h4>
            <div className="divide-y divide-gray-100">
              {list.mainboard.map((card, i) => (
                <CardRow key={`m-${i}`} card={card} zone="main" index={i} listId={list.id} />
              ))}
              {list.mainboard.length === 0 && (
                <p className="text-sm text-gray-400 italic py-2">Sin cartas</p>
              )}
            </div>
          </div>

          {/* Sideboard */}
          {list.sideboard.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Sideboard ({sideCount})
              </h4>
              <div className="divide-y divide-gray-100">
                {list.sideboard.map((card, i) => (
                  <CardRow key={`s-${i}`} card={card} zone="side" index={i} listId={list.id} />
                ))}
              </div>
            </div>
          )}

          {/* Generate PDF */}
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            {list.sideboard.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  checked={includeSide}
                  onChange={(e) => setIncludeSide(e.target.checked)}
                />
                Incluir sideboard
              </label>
            )}
            <Button onClick={() => onGeneratePdf(list, includeSide)}>
              Generar PDF
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
